export type PluralForms = {
  other: string;
} & Partial<Record<Exclude<Intl.LDMLPluralRule, 'other'>, string>>;

interface PluralizeParams {
  locale: string;
  pluralForms: PluralForms;
  quantity: number;
}

export class PluralFormatter {
  private cache: Map<string, string> = new Map();

  private selectPluralForm = ({ locale, pluralForms, quantity }: PluralizeParams): string => {
    try {
      if (!this.isLocaleSupported(locale)) {
        return pluralForms.other;
      }

      const category = new Intl.PluralRules(locale).select(quantity);
      return pluralForms[category] ?? pluralForms.other;
    } catch {
      return pluralForms.other;
    }
  };

  private createCacheKey(locale: string, pluralForms: PluralForms, quantity: number): string {
    return `${quantity}_${locale}_${Object.values(pluralForms).join(',')}`;
  }

  private isLocaleSupported(locale: string): boolean {
    return Intl.PluralRules.supportedLocalesOf([locale]).length > 0;
  }

  public getText = ({ locale, pluralForms, quantity }: PluralizeParams): string => {
    const cacheKey = this.createCacheKey(locale, pluralForms, quantity);
    const cachedText = this.cache.get(cacheKey);

    if (cachedText) return cachedText;

    const text = this.selectPluralForm({ locale, pluralForms, quantity });
    this.cache.set(cacheKey, text);

    return text;
  };
}
